import React from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {generatePath} from 'react-router-dom';

import {ModalFirsError} from 'components/common/ModalFirsError';
import {Page} from 'components/common/Page';
import {PermissionAccess} from 'components/common/PermissionAccess';
import {CheckboxArray} from 'components/ui-kit/CheckboxArray';
import {DatePicker} from 'components/ui-kit/DatePicker';
import {Input} from 'components/ui-kit/Input';
import {Link} from 'components/ui-kit/Link';
import {StatusWrapper} from 'components/ui-kit/PageComponents';
import {Select} from 'components/ui-kit/Select';
import {DOCUMENT_STATUS_COLORS} from 'core/constants/statuses';
import {DocumentStatus} from 'core/dictionaries';
import {ROUTE} from 'core/routes';
import {isCustomsRole$} from 'models/auth';
import {dictionary$} from 'models/documents/withdrawal/grid/table';
import {
    GridFieldConfig,
    UserPermission,
    WithdrawalAccessors,
    WithdrawalGridFormattedRow,
    WithdrawalGridRawRow,
} from 'types';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';

import {Controls} from '../DocumentsGrid/Controls';
import {GridsNavigation} from '../GridsNavigation';
import {ControlsWrapper, HeaderControls, NavigationWrapper} from '../MovingGrid/styled';

import {WithdrawalGridTable} from './Table';
import {TableSearch} from './Table/search';

const dictionaryOptions = (
    values: string[] | undefined,
    getLabel: (value: string) => string = (value) => value,
) => () =>
    (values ?? []).map((value, index) => ({
        id: `${index}`,
        value,
        label: getLabel(value),
    }));

const getConfig = (t: TFunction): GridFieldConfig[] => {
    const dictionary = dictionary$.getState();
    const input = <Input placeholder={t('Enter value')} />;

    return [
        {
            id: WithdrawalAccessors.id,
            header: t('pages.documents.withdrawal.columns.id'),
            filter: input,
            isSortable: false,
            width: 300,
        },
        {
            id: WithdrawalAccessors.createdDate,
            header: t('pages.documents.withdrawal.columns.createdAt'),
            filter: <DatePicker />,
            isSortable: true,
            width: 180,
        },
        {
            id: WithdrawalAccessors.status,
            header: t('pages.documents.withdrawal.columns.status'),
            filter: (
                <CheckboxArray
                    name="withdrawal-status"
                    options={dictionaryOptions(dictionary.status, (value) =>
                        t(`dictionaries.documents.status.${value}`),
                    )}
                />
            ),
            isSortable: false,
            width: 240,
        },
        {
            id: WithdrawalAccessors.reason,
            header: t('pages.documents.withdrawal.columns.reason'),
            filter: (
                <Select
                    isClearable
                    options={dictionaryOptions(dictionary.reason, (value) =>
                        t(`dictionaries.documents.DropOut.reasons.${value}`),
                    )()}
                />
            ),
            isSortable: false,
            width: 220,
        },
        {
            id: WithdrawalAccessors.codesCount,
            header: t('pages.documents.withdrawal.columns.codesCount'),
            isSortable: true,
            width: 150,
        },
        {
            id: WithdrawalAccessors.supportingDocumentNumber,
            header: t('pages.documents.withdrawal.columns.supportingDocumentNumber'),
            filter: input,
            isSortable: false,
            width: 230,
        },
        {
            id: WithdrawalAccessors.supportingDocumentDate,
            header: t('pages.documents.withdrawal.columns.supportingDocumentDate'),
            filter: <DatePicker />,
            isSortable: true,
            width: 220,
        },
        {
            id: WithdrawalAccessors.documentIssuer,
            header: t('pages.documents.withdrawal.columns.documentIssuer'),
            filter: <Select isClearable options={dictionaryOptions(dictionary.documentIssuer)()} />,
            isSortable: false,
            width: 220,
        },
        {
            id: WithdrawalAccessors.participantTin,
            header: t('pages.documents.withdrawal.columns.participantTin'),
            filter: (
                <CheckboxArray
                    searchable
                    name="withdrawal-participant-tin"
                    options={dictionaryOptions(dictionary.participantTin)}
                />
            ),
            isSortable: false,
            width: 170,
        },
        {
            id: WithdrawalAccessors.destinationCountry,
            header: t('pages.documents.withdrawal.columns.destinationCountry'),
            filter: (
                <CheckboxArray
                    searchable
                    name="withdrawal-destination-country"
                    options={dictionaryOptions(dictionary.destinationCountry)}
                />
            ),
            isSortable: false,
            width: 190,
        },
        {
            id: WithdrawalAccessors.sourceFormation,
            header: t('pages.documents.withdrawal.columns.sourceFormation'),
            filter: (
                <CheckboxArray
                    name="withdrawal-source-formation"
                    options={dictionaryOptions(dictionary.sourceFormation, (value) =>
                        t(`dictionaries.documents.DropOut.sourceFormation.${value}`),
                    )}
                />
            ),
            isSortable: false,
            width: 190,
        },
        {
            id: WithdrawalAccessors.formationProcess,
            header: t('pages.documents.withdrawal.columns.formationProcess'),
            filter: (
                <CheckboxArray
                    name="withdrawal-formation-process"
                    options={dictionaryOptions(dictionary.formationProcess, (value) =>
                        t(`pages.documents.withdrawal.formationProcess.${value}`),
                    )}
                />
            ),
            isSortable: false,
            width: 190,
        },
        {
            id: WithdrawalAccessors.employeeFullName,
            header: t('pages.documents.withdrawal.columns.employeeFullName'),
            filter: input,
            isSortable: false,
            width: 210,
        },
        {
            id: WithdrawalAccessors.organizationName,
            header: t('pages.documents.withdrawal.columns.organizationName'),
            filter: input,
            isSortable: false,
            width: 220,
        },
    ];
};

const formatOptionalDate = (value?: string) =>
    handleNil(value, (date: string) => formatDate(new Date(date), 'dd.MM.yyyy HH:mm'));

export function convertData(
    data: WithdrawalGridRawRow[] | undefined,
    t: TFunction,
    lastCreatedDocumentId?: string | null,
): WithdrawalGridFormattedRow[] {
    return (data ?? []).map((item) => ({
        [WithdrawalAccessors.id]: handleNil(item.reportId, (reportId: string) => (
            <PermissionAccess
                permissions={[UserPermission.DOCUMENT_DETAIL_VIEW]}
                placeholder={reportId}
            >
                <Link
                    to={{
                        pathname: generatePath(ROUTE.DOCUMENTS_DETAIL, {id: reportId}),
                        state: {from: ROUTE.DOCUMENTS_WITHDRAWAL},
                    }}
                >
                    <span
                        style={{
                            fontWeight: reportId === lastCreatedDocumentId ? 'bold' : 'unset',
                        }}
                    >
                        {reportId}
                    </span>
                </Link>
            </PermissionAccess>
        )),
        [WithdrawalAccessors.createdDate]: formatOptionalDate(item.createdAt),
        [WithdrawalAccessors.status]: handleNil(item.status, (status: DocumentStatus) => (
            <StatusWrapper color={DOCUMENT_STATUS_COLORS[status] ?? DOCUMENT_STATUS_COLORS.DEFAULT}>
                {t(`dictionaries.documents.status.${status}`)}
            </StatusWrapper>
        )),
        [WithdrawalAccessors.reason]: handleNil(item.reason, (reason: string) =>
            t(`dictionaries.documents.DropOut.reasons.${reason}`),
        ),
        [WithdrawalAccessors.codesCount]: handleNil(item.codesCount),
        [WithdrawalAccessors.supportingDocumentNumber]: handleNil(item.supportingDocumentNumber),
        [WithdrawalAccessors.supportingDocumentDate]: formatOptionalDate(
            item.supportingDocumentDate,
        ),
        [WithdrawalAccessors.documentIssuer]: handleNil(item.documentIssuer),
        [WithdrawalAccessors.participantTin]: handleNil(item.participantTin),
        [WithdrawalAccessors.destinationCountry]: handleNil(item.destinationCountry),
        [WithdrawalAccessors.sourceFormation]: handleNil(item.sourceFormation, (value: string) =>
            t(`dictionaries.documents.DropOut.sourceFormation.${value}`),
        ),
        [WithdrawalAccessors.formationProcess]: handleNil(item.formationProcess, (value: string) =>
            t(`pages.documents.withdrawal.formationProcess.${value}`),
        ),
        [WithdrawalAccessors.employeeFullName]: handleNil(item.employeeFullName),
        [WithdrawalAccessors.organizationName]: handleNil(item.organizationName),
    }));
}

export const WithdrawalGridPage = () => {
    const {t} = useTranslation();
    const isCustomsRole = useStore(isCustomsRole$);

    const headerControls = (
        <HeaderControls>
            <ControlsWrapper>
                <TableSearch />
                {!isCustomsRole && <Controls />}
            </ControlsWrapper>
            <NavigationWrapper needMargin>
                {!isCustomsRole && <GridsNavigation />}
            </NavigationWrapper>
        </HeaderControls>
    );

    return (
        <Page title={t('menu.Documents')}>
            <ModalFirsError />
            <WithdrawalGridTable
                configFunc={getConfig}
                dataMapper={convertData}
                headerControls={headerControls}
            />
        </Page>
    );
};
